import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

const API_BASE =
  process.env.REACT_APP_API_URL || "";

const HOME_SCREEN_REWARD = 25;

export default function AddHomeScreen({
  user,
  setUser,
  onNavigate,
}) {
  const [status, setStatus] =
    useState("checking");

  const [busy, setBusy] =
    useState(false);

  const [rewarded, setRewarded] =
    useState(false);

  const [
    rewardStateLoaded,
    setRewardStateLoaded,
  ] = useState(false);

  const [message, setMessage] =
    useState("");

  const claimInFlightRef =
    useRef(false);

  const tg =
    window?.Telegram?.WebApp;


  /*
   * ============================================================
   * LOAD REWARD STATE FROM BACKEND
   * ============================================================
   *
   * This checks finance_ledger through the backend.
   *
   * IMPORTANT:
   *
   * Telegram Home Screen status and reward status are
   * completely separate things.
   *
   * Example:
   *
   * rewarded = true
   * status = missed
   *
   * Means:
   *
   * User already earned 25 Runes previously,
   * but removed TapTopia from the Home Screen.
   *
   * In this situation we allow ADD AGAIN,
   * but NEVER reward again.
   * ============================================================
   */

  const loadRewardState =
    useCallback(async () => {
      try {
        const token =
          localStorage.getItem(
            "token"
          );

        const res = await fetch(
          `${API_BASE}/api/tasks/home-screen/state`,
          {
            method: "GET",

            headers: {
              ...(token
                ? {
                    Authorization:
                      `Bearer ${token}`,
                  }
                : {}),

              "X-TG-INITDATA":
                window.Telegram
                  ?.WebApp
                  ?.initData || "",
            },
          }
        );

        const data =
          await res
            .json()
            .catch(() => ({}));

        if (!res.ok) {
          console.error(
            "[HOME SCREEN STATE]",
            data
          );

          return;
        }

        console.log(
          "[HOME SCREEN STATE]",
          data
        );

        setRewarded(
          data?.claimed === true
        );
      } catch (err) {
        console.error(
          "[HOME SCREEN STATE ERROR]",
          err
        );
      } finally {
        setRewardStateLoaded(
          true
        );
      }
    }, []);


  /*
   * ============================================================
   * CLAIM REWARD
   * ============================================================
   */

  const rewardUser =
    useCallback(async () => {

      /*
       * Frontend protection.
       *
       * If we already know the user received
       * the reward, don't even call backend.
       */

      if (rewarded) {
        console.log(
          "[HOME SCREEN REWARD] Already rewarded - skipping"
        );

        return;
      }


      /*
       * Prevent duplicate requests when:
       *
       * checkHomeScreenStatus
       * +
       * homeScreenAdded
       * +
       * homeScreenChecked
       *
       * happen almost together.
       */

      if (
        claimInFlightRef.current
      ) {
        return;
      }

      claimInFlightRef.current =
        true;

      try {
        setBusy(true);
        setMessage("");

        const token =
          localStorage.getItem(
            "token"
          );

        const res = await fetch(
          `${API_BASE}/api/tasks/home-screen/claim`,
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",

              ...(token
                ? {
                    Authorization:
                      `Bearer ${token}`,
                  }
                : {}),

              "X-TG-INITDATA":
                window.Telegram
                  ?.WebApp
                  ?.initData || "",
            },

            body: JSON.stringify({
              source:
                "telegram_home_screen",
            }),
          }
        );

        const data =
          await res
            .json()
            .catch(() => ({}));

        console.log(
          "[HOME SCREEN CLAIM]",
          {
            status:
              res.status,
            data,
          }
        );


        if (!res.ok) {
          throw new Error(
            data?.message ||
              data?.error ||
              "Unable to claim reward."
          );
        }


        /*
         * From this point onward, regardless
         * of whether it was newly claimed or
         * already existed in finance_ledger,
         * this user is considered rewarded.
         */

        setRewarded(true);


        /*
         * Update current Runes balance.
         */

        if (
          data?.user?.runes !=
          null
        ) {
          setUser?.(
            (prev) => ({
              ...prev,

              runes:
                Number(
                  data.user
                    .runes
                ),
            })
          );
        }


        /*
         * Backend may say the reward was
         * already claimed previously.
         */

        if (
          data?.alreadyClaimed
        ) {
          setMessage(
            "Reward already claimed."
          );

          return;
        }


        /*
         * New reward.
         */

        setMessage(
          `${HOME_SCREEN_REWARD} Runes added! 🎉`
        );


        try {
          tg?.HapticFeedback
            ?.notificationOccurred
            ?.("success");
        } catch (_) {}

      } catch (err) {

        console.error(
          "[HOME SCREEN REWARD ERROR]",
          err
        );

        setMessage(
          err?.message ||
            "Something went wrong."
        );


        try {
          tg?.HapticFeedback
            ?.notificationOccurred
            ?.("error");
        } catch (_) {}

      } finally {

        claimInFlightRef.current =
          false;

        setBusy(false);

      }
    }, [
      rewarded,
      setUser,
      tg,
    ]);


  /*
   * ============================================================
   * HANDLE SUCCESSFUL HOME SCREEN ADD
   * ============================================================
   *
   * Centralized here because Telegram may tell us about the
   * installation through more than one mechanism.
   * ============================================================
   */

  const handleSuccessfullyAdded =
    useCallback(() => {

      console.log(
        "[HOME SCREEN] Successfully added"
      );

      setStatus("added");


      /*
       * IMPORTANT:
       *
       * If user already received reward previously,
       * this may simply be a re-installation.
       *
       * In that situation:
       *
       * DO NOT call backend.
       * DO NOT give reward.
       */

      if (!rewarded) {
        rewardUser();
      } else {
        console.log(
          "[HOME SCREEN] Re-added after reward - no reward"
        );
      }

    }, [
      rewarded,
      rewardUser,
    ]);


  /*
   * ============================================================
   * CHECK TELEGRAM HOME SCREEN STATUS
   * ============================================================
   */

  const checkStatus =
    useCallback(() => {

      if (!tg) {
        console.warn(
          "[HOME SCREEN] Telegram WebApp unavailable"
        );

        setStatus(
          "unsupported"
        );

        return;
      }


      if (
        typeof tg
          .checkHomeScreenStatus !==
        "function"
      ) {
        console.warn(
          "[HOME SCREEN] checkHomeScreenStatus unavailable"
        );

        setStatus(
          "unsupported"
        );

        return;
      }


      try {

        tg.checkHomeScreenStatus(
          (homeStatus) => {

            const nextStatus =
              homeStatus ||
              "unknown";

            console.log(
              "[HOME SCREEN STATUS]",
              nextStatus
            );


            setStatus(
              nextStatus
            );


            /*
             * Telegram confirms it currently
             * exists on Home Screen.
             */

            if (
              nextStatus ===
              "added"
            ) {

              /*
               * Only reward if this account
               * has never been rewarded.
               */

              if (!rewarded) {
                rewardUser();
              }

            }

          }
        );

      } catch (err) {

        console.error(
          "[HOME SCREEN CHECK ERROR]",
          err
        );

        setStatus(
          "unknown"
        );

      }

    }, [
      tg,
      rewarded,
      rewardUser,
    ]);


  /*
   * ============================================================
   * LOAD BACKEND REWARD STATE
   * ============================================================
   */

  useEffect(() => {

    loadRewardState();

  }, [
    loadRewardState,
  ]);


  /*
   * ============================================================
   * CHECK TELEGRAM STATUS
   *
   * We wait until finance_ledger state is loaded first.
   *
   * This is important.
   *
   * Otherwise:
   *
   * 1. Telegram might instantly say "added"
   * 2. rewarded is still false because API hasn't returned yet
   * 3. frontend unnecessarily tries to claim again
   *
   * Backend would still protect us, but there is no reason to
   * make that request.
   * ============================================================
   */

  useEffect(() => {

    if (!rewardStateLoaded) {
      return;
    }


    if (!tg) {

      setStatus(
        "unsupported"
      );

      return;
    }


    checkStatus();

  }, [
    rewardStateLoaded,
    tg,
    checkStatus,
  ]);


  /*
   * ============================================================
   * TELEGRAM EVENTS
   * ============================================================
   */

  useEffect(() => {

    if (!tg) {
      return;
    }


    /*
     * Telegram confirms that the Mini App
     * was successfully added.
     */

    const handleAdded =
      () => {

        console.log(
          "[HOME SCREEN EVENT] homeScreenAdded"
        );

        handleSuccessfullyAdded();

      };


    /*
     * Telegram may also send a status event.
     */

    const handleChecked =
      (event) => {

        const nextStatus =
          event?.status ||
          event ||
          "unknown";


        console.log(
          "[HOME SCREEN EVENT] homeScreenChecked",
          nextStatus
        );


        setStatus(
          nextStatus
        );


        if (
          nextStatus ===
          "added"
        ) {

          /*
           * Again:
           *
           * Reward only if this user has
           * never received the reward.
           */

          if (!rewarded) {
            rewardUser();
          }

        }

      };


    tg.onEvent?.(
      "homeScreenAdded",
      handleAdded
    );


    tg.onEvent?.(
      "homeScreenChecked",
      handleChecked
    );


    return () => {

      tg.offEvent?.(
        "homeScreenAdded",
        handleAdded
      );


      tg.offEvent?.(
        "homeScreenChecked",
        handleChecked
      );

    };

  }, [
    tg,
    rewarded,
    rewardUser,
    handleSuccessfullyAdded,
  ]);


  /*
   * ============================================================
   * ADD / ADD AGAIN BUTTON
   * ============================================================
   *
   * SAME function is used for:
   *
   * ADD AND EARN 25
   *
   * and
   *
   * ADD AGAIN
   *
   * The reward decision happens separately.
   * ============================================================
   */

  const handleAdd =
    () => {

      if (!tg) {

        setMessage(
          "Telegram WebApp is not available."
        );

        return;
      }


      if (
        typeof tg
          .addToHomeScreen !==
        "function"
      ) {

        setStatus(
          "unsupported"
        );

        setMessage(
          "Adding TapTopia to the Home Screen is not supported by this Telegram version."
        );

        return;
      }


      try {

        setMessage("");


        tg.HapticFeedback
          ?.impactOccurred
          ?.("medium");


        /*
         * Telegram requires this to happen
         * directly from a user interaction.
         */

        tg.addToHomeScreen();

      } catch (err) {

        console.error(
          "[ADD HOME SCREEN ERROR]",
          err
        );


        setMessage(
          "Unable to open Home Screen installation."
        );

      }
    };


  /*
   * ============================================================
   * UI STATE
   * ============================================================
   */

  const alreadyAdded =
    status === "added";


  const unsupported =
    status ===
    "unsupported";


  const checking =
    status ===
      "checking" ||
    !rewardStateLoaded;


  /*
   * ============================================================
   * BUTTON BEHAVIOR
   * ============================================================
   *
   * rewarded false
   * + not added
   *
   * => ADD AND EARN 25
   *
   *
   * rewarded false
   * + added
   *
   * => CLAIM 25 RUNES
   *
   *
   * rewarded true
   * + added
   *
   * => ADDED TO HOME SCREEN
   *
   *
   * rewarded true
   * + missed / unknown
   *
   * => ADD AGAIN
   *
   * ============================================================
   */


  return (

    <div className="home-screen-page">


      <div className="home-screen-top">

        <button
          type="button"
          className="home-screen-back"
          onClick={() =>
            onNavigate?.(
              "arenas"
            )
          }
        >

          <img
            src="/v2/close.png"
            alt="Close"
            style={{
              width: 32,
              height: 32,
            }}
          />

        </button>


        <div className="home-screen-back-placeholder" />

      </div>


      <div className="home-screen-bottom">


        {checking ? (

          /*
           * ====================================================
           * LOADING
           * ====================================================
           */

          <button
            type="button"
            className="home-screen-button"
            disabled
          >

            CHECKING...

          </button>


        ) : rewarded && alreadyAdded ? (

          /*
           * ====================================================
           * ALREADY REWARDED + CURRENTLY INSTALLED
           * ====================================================
           */

          <button
            type="button"
            className="home-screen-button completed"
            disabled
          >

            ✓ ADDED TO HOME SCREEN

          </button>


        ) : unsupported ? (

          /*
           * ====================================================
           * TELEGRAM VERSION DOESN'T SUPPORT IT
           * ====================================================
           */

          <button
            type="button"
            className="home-screen-button disabled"
            disabled
          >

            NOT SUPPORTED

          </button>


        ) : rewarded && !alreadyAdded ? (

          /*
           * ====================================================
           * USER ALREADY RECEIVED REWARD
           * BUT REMOVED THE HOME SCREEN ICON
           *
           * Allow them to install it again.
           *
           * NO REWARD.
           * ====================================================
           */

          <button
            type="button"
            className="home-screen-button"
            onClick={handleAdd}
            disabled={busy}
          >

            ADD AGAIN

          </button>


        ) : alreadyAdded ? (

          /*
           * ====================================================
           * INSTALLED BUT REWARD HAS NOT BEEN CLAIMED YET
           * ====================================================
           */

          <button
            type="button"
            className="home-screen-button"
            onClick={rewardUser}
            disabled={busy}
          >

            {busy
              ? "CLAIMING..."
              : "CLAIM 25 RUNES"}


            {!busy && (

              <img
                src="/runes.png"
                alt=""
              />

            )}

          </button>


        ) : (

          /*
           * ====================================================
           * FIRST TIME
           *
           * Not installed.
           * Never rewarded.
           * ====================================================
           */

          <button
            type="button"
            className="home-screen-button"
            onClick={handleAdd}
            disabled={busy}
          >

            ADD AND EARN 25


            <img
              src="/runes.png"
              alt=""
            />

          </button>

        )}


        {message && (

          <div className="home-screen-message">

            {message}

          </div>

        )}

      </div>

    </div>

  );
}